/**
 * Dice Registry Store - State management for dice registration flow
 * Manages registration wizard state, face captures, and API interactions
 */

import {
  DiceType,
  DiceRegistration,
  UploadUrl,
  FACE_COUNTS,
  CaptureMode,
  FaceCoverage,
  RollCapture,
  IMAGE_REQUIREMENTS,
  TrainingIntensity,
  DEFAULT_DEGREES_BY_MODE,
  calculateCapturesFromDegrees,
  type DegreesPerCapture
} from '@dice-capture/shared/types/dice-registry';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { bridgeService } from '@/services/bridgeService';
import { diceRegistryService } from '@/services/diceRegistryService';
import { createScopedReporter } from '@/services/errorReporter';
import { ftpSettingsService } from '@/services/ftpSettingsService';
import { localDownloadService } from '@/services/localDownloadService';
import { connectionManager } from '@/services/connectionManager';
import type { TrainingStatusMessage } from '@/services/websocket';
import { useSoulBondsStore } from '@/stores/soulBondsStore';
import { useAuthStore } from '@/stores/authStore';
import { useProviderConfigStore } from '@/stores/providerConfigStore';

const SERVICE_TAG = '[DICE_REGISTRY]';
const reporter = createScopedReporter('DICE_REGISTRY');

/**
 * Individual image captured for a face during registration
 */
export interface FaceImage {
  /** Unique ID for tracking and deletion */
  id: string;
  /** Image data */
  blob: Blob;
  /** Object URL for thumbnail display */
  thumbnailUrl: string;
  /** When this image was captured */
  capturedAt: string;
  /** Rotation position (0-based) when captured */
  rotationIndex: number;
  /** S3 key for cloud-stored images (only set for cloud/both storage modes) */
  s3Key?: string;
}

/**
 * Face capture data during registration
 */
export interface FaceCaptureData {
  faceNumber: number;
  /** Array of captured images for this face */
  images: FaceImage[];
  /** Legacy single blob support - deprecated, use images array */
  imageBlob: Blob | null;
  uploadUrl: string;
  uploaded: boolean;
}

/**
 * Registration wizard step
 */
export type RegistrationStep = 'select' | 'capturing' | 'reviewing' | 'training';

/**
 * Training progress state
 */
export interface TrainingProgress {
  registrationId: string;
  status: 'started' | 'progress' | 'complete' | 'failed';
  /** 0-100 percentage */
  progress: number;
  modelArn?: string;
  error?: string;
}

/**
 * Current registration state (in progress)
 */
interface CurrentRegistration {
  registrationId: string | null;
  diceType: DiceType | null;
  diceName: string;
  currentFace: number;
  totalFaces: number;
  faces: FaceCaptureData[];
  step: RegistrationStep;
  /** Capture mode: manual face-by-face or video rolling */
  captureMode: CaptureMode;
  /** Roll captures during video mode */
  rolls: RollCapture[];
  /** Coverage tracking per face for video mode */
  faceCoverage: FaceCoverage[];
  /** Total images needed based on dice type */
  totalImagesNeeded: number;
  /** Total images captured across all faces */
  totalImagesCaptured: number;
  /** Current rotation position within current face (0-based) */
  currentRotation: number;
  /** Degrees to rotate between captures (e.g., 45 = 8 captures per face) */
  degreesPerCapture: DegreesPerCapture;
  /** Computed: Number of captures per face based on degreesPerCapture */
  capturesPerFace: number;
  /** TICKET-095: Flag indicating face progression is pending user confirmation */
  faceProgressionPending: boolean;
}

interface DiceRegistryState {
  // Current registration in progress
  currentRegistration: CurrentRegistration;

  // All user's completed registrations
  registrations: DiceRegistration[];

  // Device ID (persisted)
  deviceId: string | null;

  // Training intensity mode (recommended or light)
  trainingIntensity: TrainingIntensity;

  // Auto-capture mode for registration (TICKET-071)
  // When enabled, captures automatically when dice settles
  autoCaptureEnabled: boolean;

  // Degrees per capture preference (TICKET-117)
  // Persisted user preference for rotation degrees (applies to all dice types)
  degreesPerCapturePreference: DegreesPerCapture | null;

  // Edit mode state (TICKET-088)
  // When true, editing an existing registration rather than creating new
  isEditMode: boolean;

  // Training progress for subscribed registration
  trainingProgress: TrainingProgress | null;

  // WebSocket subscription state
  trainingSubscriptionId: string | null;

  // Loading states
  isLoading: boolean;
  /** Tracks which registrationId is currently being deleted (null if none) */
  deletingRegistrationId: string | null;
  error: string | null;

  // Actions
  setDeviceId: (deviceId: string) => void;
  setDiceType: (type: DiceType) => void;
  setDiceName: (name: string) => void;
  setTrainingIntensity: (intensity: TrainingIntensity) => void;
  startRegistration: () => Promise<void>;
  captureCurrentFace: (imageBlob: Blob) => Promise<void>;
  retakeFace: (faceNumber: number) => void;
  goToFaceSelection: () => void;
  completeRegistration: () => Promise<void>;
  fetchRegistrations: () => Promise<void>;
  deleteRegistration: (registrationId: string) => Promise<void>;
  resetCurrentRegistration: () => void;
  setError: (error: string | null) => void;

  // WebSocket training status subscription
  subscribeToTrainingStatus: (registrationId: string) => void;
  unsubscribeFromTrainingStatus: () => void;

  // Video capture mode actions
  setCaptureMode: (mode: CaptureMode) => void;
  recordRoll: (capture: RollCapture) => void;
  updateFaceCoverage: (faceNumber: number, count: number) => void;
  getMissingFaces: () => FaceCoverage[];

  // Rotation guidance actions
  advanceRotation: () => void;
  /** TICKET-095: Confirm face progression after minimum images reached */
  confirmFaceProgression: () => void;
  /** TICKET-095: Dismiss face progression prompt and stay on current face */
  dismissFaceProgression: () => void;

  // Multi-image capture actions
  addImageToFace: (faceNumber: number, imageBlob: Blob) => Promise<void>;
  removeImageFromFace: (faceNumber: number, imageId: string) => Promise<void>;
  /** Replace an image on a face with a new cropped version (TICKET-227) */
  replaceImageOnFace: (faceNumber: number, imageId: string, newBlob: Blob) => Promise<void>;
  getImagesForFace: (faceNumber: number) => FaceImage[];
  getTotalImageCount: () => number;

  // Auto-capture mode (TICKET-071)
  setAutoCaptureEnabled: (enabled: boolean) => void;

  // Rotation customization (TICKET-117)
  setDegreesPerCapture: (degrees: DegreesPerCapture) => void;

  // Edit mode actions (TICKET-088)
  startEditMode: (deviceId: string, diceType: DiceType) => Promise<void>;
  exitEditMode: () => void;
}

const initialCurrentRegistration: CurrentRegistration = {
  registrationId: null,
  diceType: null,
  diceName: '',
  currentFace: 1,
  totalFaces: 0,
  faces: [],
  step: 'select',
  captureMode: 'manual',
  rolls: [],
  faceCoverage: [],
  totalImagesNeeded: 0,
  totalImagesCaptured: 0,
  currentRotation: 0,
  degreesPerCapture: 45,
  capturesPerFace: 8,
  faceProgressionPending: false
};

const initialState = {
  currentRegistration: initialCurrentRegistration,
  registrations: [],
  deviceId: null,
  trainingIntensity: 'recommended' as TrainingIntensity,
  autoCaptureEnabled: true, // TICKET-071: Default to ON
  degreesPerCapturePreference: null as DegreesPerCapture | null, // TICKET-117: User's preferred degrees
  isEditMode: false, // TICKET-088: Edit mode flag
  trainingProgress: null,
  trainingSubscriptionId: null,
  isLoading: false,
  deletingRegistrationId: null,
  error: null
};

/**
 * Merge user-level and device-level registrations.
 * User-level records take priority on duplicate registrationId.
 */
export function mergeRegistrations(
  userRegs: DiceRegistration[],
  deviceRegs: DiceRegistration[]
): DiceRegistration[] {
  const merged = new Map<string, DiceRegistration>();
  for (const reg of userRegs) {
    merged.set(reg.registrationId, reg);
  }
  for (const reg of deviceRegs) {
    if (!merged.has(reg.registrationId)) {
      merged.set(reg.registrationId, reg);
    }
  }
  return Array.from(merged.values());
}

export const useDiceRegistryStore = create<DiceRegistryState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setDeviceId: (deviceId) => set({ deviceId }),

      setDiceType: (type) => {
        const totalFaces = FACE_COUNTS[type];
        const trainingIntensity = get().trainingIntensity;
        const { degreesPerCapturePreference } = get();

        // TICKET-117: Use saved preference if exists, otherwise use mode-based default
        // TICKET-180: 'unlimited' mode has no degree default — fall back to 45
        const degreesPerCapture = degreesPerCapturePreference
          ?? (trainingIntensity !== 'unlimited' ? DEFAULT_DEGREES_BY_MODE[trainingIntensity] : 45);
        const capturesPerFace = calculateCapturesFromDegrees(degreesPerCapture);

        console.info(`${SERVICE_TAG} Dice type selected`, {
          type,
          totalFaces,
          degreesPerCapture,
          capturesPerFace,
          trainingIntensity,
          usingSavedPreference: !!degreesPerCapturePreference
        });

        set((state) => ({
          currentRegistration: {
            ...initialCurrentRegistration,
            diceType: type,
            totalFaces,
            degreesPerCapture,
            capturesPerFace,
            diceName: state.currentRegistration.diceName
          }
        }));
      },

      setDiceName: (name) => {
        set((state) => ({
          currentRegistration: {
            ...state.currentRegistration,
            diceName: name
          }
        }));
      },

      setTrainingIntensity: (intensity: TrainingIntensity) => {
        console.info(`${SERVICE_TAG} Training intensity set`, { intensity });
        set({ trainingIntensity: intensity });
      },

      startRegistration: async () => {
        const { deviceId, currentRegistration } = get();

        if (!deviceId) {
          reporter.error('No device ID', { showToast: true });
          set({ error: 'No device ID. Please refresh and try again.' });
          return;
        }

        if (!currentRegistration.diceType) {
          reporter.error('No dice type selected', { showToast: false });
          set({ error: 'Please select a dice type.' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          console.info(`${SERVICE_TAG} Starting registration`, {
            deviceId,
            diceType: currentRegistration.diceType,
            diceName: currentRegistration.diceName
          });

          const response = await diceRegistryService.startRegistration(
            deviceId,
            currentRegistration.diceType,
            currentRegistration.diceName || undefined
          );

          // Initialize face capture data with upload URLs and empty images array
          const faces: FaceCaptureData[] = response.uploadUrls.map((url: UploadUrl) => ({
            faceNumber: url.face,
            images: [],
            imageBlob: null,
            uploadUrl: url.url,
            uploaded: false
          }));

          set((state) => ({
            currentRegistration: {
              ...state.currentRegistration,
              registrationId: response.registrationId,
              faces,
              currentFace: 1,
              step: 'capturing'
            },
            isLoading: false
          }));

          console.info(`${SERVICE_TAG} Registration started`, {
            registrationId: response.registrationId,
            uploadUrlCount: faces.length
          });
        } catch (error) {
          reporter.error('Failed to start registration', {
            originalError: error instanceof Error ? error : undefined
          });
          set({
            error: 'Failed to start registration. Please try again.',
            isLoading: false
          });
        }
      },

      captureCurrentFace: async (imageBlob) => {
        const { deviceId, currentRegistration } = get();
        const { currentFace, faces, totalFaces, diceType } = currentRegistration;

        const faceData = faces.find((f) => f.faceNumber === currentFace);
        if (!faceData) {
          console.error(`${SERVICE_TAG} Face data not found`, { currentFace });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // Get storage configuration
          const { appSettings } = useSoulBondsStore.getState();
          const storageMode = appSettings.storageConfig?.mode || 'cloud';
          const ftpConfigured = appSettings.storageConfig?.ftpConfigured || false;
          const bridgeConfigured = appSettings.storageConfig?.bridgeConfigured || false;
          const bridgeUrl = appSettings.storageConfig?.bridgeUrl;
          const { currentRotation } = currentRegistration;

          console.info(`${SERVICE_TAG} Uploading face`, {
            faceNumber: currentFace,
            totalFaces,
            blobSize: imageBlob.size,
            storageMode,
            storageConfigRaw: appSettings.storageConfig,
            ftpConfigured,
            bridgeConfigured,
            rotationIndex: currentRotation,
            hasUploadUrl: !!faceData.uploadUrl,
            uploadUrlLength: faceData.uploadUrl?.length || 0
          });

          // Upload to cloud (S3) if configured
          // Use unique presigned URL per image to prevent overwrites (TICKET-087)
          let uploadedS3Key: string | undefined;
          if (storageMode === 'cloud' || storageMode === 'both') {
            if (!currentRegistration.registrationId) {
              console.error(`${SERVICE_TAG} Cannot upload - no registrationId`);
              set({ isLoading: false, error: 'Registration not started properly' });
              return;
            }

            const existingImageCount = faceData.images.length;
            const imageIndex = existingImageCount + 1;

            console.info(`${SERVICE_TAG} Getting presigned URL for face ${currentFace}, image ${imageIndex}...`);
            const { uploadUrl, s3Key } = await diceRegistryService.getImageUploadUrl(
              currentRegistration.registrationId,
              currentFace,
              imageIndex
            );

            console.info(`${SERVICE_TAG} Starting S3 upload for face ${currentFace}...`);
            await diceRegistryService.uploadFace(uploadUrl, imageBlob);
            console.info(`${SERVICE_TAG} Face uploaded to cloud`, { faceNumber: currentFace, s3Key });

            uploadedS3Key = s3Key;
          } else {
            console.warn(`${SERVICE_TAG} S3 upload SKIPPED - storageMode is '${storageMode}', not 'cloud' or 'both'`);
          }

          // Upload to bridge server if configured (direct local network upload)
          if (storageMode === 'bridge' && bridgeConfigured && bridgeUrl && deviceId && diceType) {
            try {
              const bridgeResult = await bridgeService.uploadImage(
                bridgeUrl,
                deviceId,
                diceType,
                currentFace,
                imageBlob,
                currentRotation
              );
              console.info(`${SERVICE_TAG} Face uploaded to bridge`, {
                faceNumber: currentFace,
                rotationIndex: currentRotation,
                path: bridgeResult.path,
                size: bridgeResult.size
              });
            } catch (bridgeError) {
              console.error(`${SERVICE_TAG} Bridge upload failed`, {
                faceNumber: currentFace,
                error: bridgeError instanceof Error ? bridgeError.message : 'Unknown error'
              });
              throw bridgeError;
            }
          }

          // Upload to FTP if configured
          if ((storageMode === 'ftp' || storageMode === 'both') && ftpConfigured && deviceId && diceType) {
            try {
              const ftpResult = await ftpSettingsService.uploadToFtp(
                deviceId,
                diceType,
                currentFace,
                imageBlob
              );
              console.info(`${SERVICE_TAG} Face uploaded to FTP`, {
                faceNumber: currentFace,
                remotePath: ftpResult.remotePath
              });
            } catch (ftpError) {
              // Log FTP error but don't fail if "both" mode and cloud succeeded
              console.warn(`${SERVICE_TAG} FTP upload failed`, {
                faceNumber: currentFace,
                error: ftpError instanceof Error ? ftpError.message : 'Unknown error'
              });
              if (storageMode === 'ftp') {
                // FTP-only mode: rethrow the error
                throw ftpError;
              }
              // "both" mode: continue with cloud success
            }
          }

          // Download to device if configured (saves to phone's downloads folder)
          if (storageMode === 'download' && deviceId && diceType) {
            try {
              const downloadResult = await localDownloadService.downloadImage(
                deviceId,
                diceType,
                currentFace,
                imageBlob,
                currentRotation
              );
              console.info(`${SERVICE_TAG} Face downloaded to device`, {
                faceNumber: currentFace,
                rotationIndex: currentRotation,
                filename: downloadResult.filename,
                size: downloadResult.size
              });
            } catch (downloadError) {
              console.error(`${SERVICE_TAG} Download failed`, {
                faceNumber: currentFace,
                error: downloadError instanceof Error ? downloadError.message : 'Unknown error'
              });
              throw downloadError;
            }
          }

          // Create new image entry for multi-image support
          const imageId = crypto.randomUUID();

          const newImage: FaceImage = {
            id: imageId,
            blob: imageBlob,
            thumbnailUrl: URL.createObjectURL(imageBlob),
            capturedAt: new Date().toISOString(),
            rotationIndex: currentRotation,
            s3Key: uploadedS3Key // Use unique S3 key from backend (TICKET-087)
          };

          // Update state with new image added to the face's images array
          const updatedFaces = faces.map((f) =>
            f.faceNumber === currentFace
              ? {
                ...f,
                images: [...f.images, newImage],
                imageBlob,
                uploaded: true
              }
              : f
          );

          const isLastFace = currentFace === totalFaces;

          set((state) => ({
            currentRegistration: {
              ...state.currentRegistration,
              faces: updatedFaces,
              currentFace: isLastFace ? currentFace : currentFace + 1,
              step: isLastFace ? 'reviewing' : 'capturing',
              totalImagesCaptured: state.currentRegistration.totalImagesCaptured + 1
            },
            isLoading: false
          }));

          console.info(`${SERVICE_TAG} Face uploaded`, {
            faceNumber: currentFace,
            isLastFace,
            nextFace: isLastFace ? null : currentFace + 1
          });
        } catch (error) {
          console.error(`${SERVICE_TAG} Failed to upload face`, {
            error: error instanceof Error ? error.message : 'Unknown error',
            faceNumber: currentFace
          });
          set({
            error: `Failed to upload face ${currentFace}. Please try again.`,
            isLoading: false
          });
        }
      },

      retakeFace: (faceNumber) => {
        console.info(`${SERVICE_TAG} Retaking face`, { faceNumber });

        set((state) => ({
          currentRegistration: {
            ...state.currentRegistration,
            currentFace: faceNumber,
            step: 'capturing'
          }
        }));
      },

      goToFaceSelection: () => {
        console.info(`${SERVICE_TAG} Going to face selection menu`);

        set((state) => ({
          currentRegistration: {
            ...state.currentRegistration,
            step: 'reviewing'
          }
        }));
      },

      completeRegistration: async () => {
        const { currentRegistration } = get();

        if (!currentRegistration.registrationId) {
          console.error(`${SERVICE_TAG} No registration ID`);
          return;
        }

        // Verify all faces have at least one image
        const allHaveImages = currentRegistration.faces.every((f) => f.images.length > 0);
        if (!allHaveImages) {
          const missing = currentRegistration.faces.filter((f) => f.images.length === 0);
          console.warn(`${SERVICE_TAG} Not all faces have images`, {
            missing: missing.map((f) => f.faceNumber)
          });
          set({ error: 'Please capture at least one image for each face before completing.' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          console.info(`${SERVICE_TAG} Completing registration`, {
            registrationId: currentRegistration.registrationId
          });

          const response = await diceRegistryService.completeRegistration(
            currentRegistration.registrationId
          );

          if (response.status === 'failed') {
            throw new Error(response.message);
          }

          set((state) => ({
            currentRegistration: {
              ...state.currentRegistration,
              step: 'training'
            },
            isLoading: false
          }));

          // Refresh registrations list
          get().fetchRegistrations();

          console.info(`${SERVICE_TAG} Registration completed`, {
            status: response.status,
            estimatedTime: response.estimatedTrainingTime
          });
        } catch (error) {
          reporter.error('Failed to complete registration', {
            originalError: error instanceof Error ? error : undefined
          });
          set({
            error: 'Failed to complete registration. Please try again.',
            isLoading: false
          });
        }
      },

      fetchRegistrations: async () => {
        const { deviceId } = get();

        if (!deviceId) {
          reporter.warning('No device ID, skipping fetch', { showToast: false });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const { isAuthenticated } = useAuthStore.getState();

          if (isAuthenticated) {
            // Try userId-based lookup first (cross-device)
            const myData = await diceRegistryService.getMyRegistrations(deviceId);
            if (myData) {
              // Also fetch device-specific (catches unclaimed records on this device)
              const deviceRegs = await diceRegistryService.getRegistrations(deviceId);
              // Merge: userId-fetched records win on duplicate registrationId
              const merged = mergeRegistrations(myData.registrations, deviceRegs);
              set({ registrations: merged, isLoading: false });

              // Bulk-populate provider configs
              if (myData.providerConfigs.length > 0) {
                useProviderConfigStore.getState().bulkSetConfigs(myData.providerConfigs);
              }

              console.info(`${SERVICE_TAG} Registrations fetched (cross-device)`, {
                userCount: myData.registrations.length,
                deviceCount: deviceRegs.length,
                mergedCount: merged.length
              });
              return;
            }
            // Fallback: myRegistrations endpoint not available, use device-only
            console.info(`${SERVICE_TAG} Falling back to device-only registration lookup`);
          }

          // Guest mode or fallback: device-only lookup (existing behavior)
          console.info(`${SERVICE_TAG} Fetching registrations`, { deviceId });
          const registrations = await diceRegistryService.getRegistrations(deviceId);
          set({ registrations, isLoading: false });

          console.info(`${SERVICE_TAG} Registrations fetched`, {
            count: registrations.length
          });
        } catch (error) {
          reporter.error('Failed to fetch registrations', {
            originalError: error instanceof Error ? error : undefined,
            showToast: false
          });
          set({
            error: 'Failed to fetch registrations.',
            isLoading: false
          });
        }
      },

      deleteRegistration: async (registrationId) => {
        const { registrations } = get();

        const registration = registrations.find((r) => r.registrationId === registrationId);
        if (!registration) {
          console.error(`${SERVICE_TAG} Registration not found`, { registrationId });
          return;
        }

        const { deviceId, diceType } = registration;

        set({ deletingRegistrationId: registrationId, error: null });

        try {
          console.info(`${SERVICE_TAG} Deleting registration`, {
            registrationId,
            deviceId,
            diceType
          });

          await diceRegistryService.deleteRegistration(deviceId, diceType);

          // Remove from local state
          set((state) => ({
            registrations: state.registrations.filter(
              (r) => r.registrationId !== registrationId
            ),
            deletingRegistrationId: null
          }));

          console.info(`${SERVICE_TAG} Registration deleted`, { registrationId, diceType });
        } catch (error) {
          reporter.error('Failed to delete registration', {
            originalError: error instanceof Error ? error : undefined
          });
          set({
            error: 'Failed to delete registration.',
            deletingRegistrationId: null
          });
        }
      },

      resetCurrentRegistration: () => {
        console.info(`${SERVICE_TAG} Resetting current registration`);
        // Unsubscribe from training status when resetting
        get().unsubscribeFromTrainingStatus();

        // Revoke all object URLs before reset to prevent memory leaks
        const { currentRegistration } = get();
        currentRegistration.faces.forEach((face) => {
          face.images.forEach((img) => {
            if (img.thumbnailUrl) {
              URL.revokeObjectURL(img.thumbnailUrl);
            }
          });
        });

        set({ currentRegistration: initialCurrentRegistration });
      },

      setError: (error) => set({ error }),

      subscribeToTrainingStatus: (registrationId: string) => {
        const { trainingSubscriptionId } = get();

        // Already subscribed to this registration
        if (trainingSubscriptionId === registrationId) {
          console.info(`${SERVICE_TAG} Already subscribed to training status`, {
            registrationId
          });
          return;
        }

        // Unsubscribe from any existing subscription
        if (trainingSubscriptionId) {
          get().unsubscribeFromTrainingStatus();
        }

        console.info(`${SERVICE_TAG} Subscribing to training status`, {
          registrationId
        });

        // Subscribe to training status via connectionManager across all active WebSocket clients
        connectionManager.subscribeToTrainingStatus((message: TrainingStatusMessage) => {
            // Only process messages for the subscribed registration
            if (message.registrationId !== registrationId) {
              return;
            }

            console.info(`${SERVICE_TAG} Received training status update`, {
              registrationId: message.registrationId,
              status: message.status,
              progress: message.progress
            });

            const newProgress: TrainingProgress = {
              registrationId: message.registrationId,
              status: message.status,
              progress: message.progress ?? 0,
              modelArn: message.modelArn,
              error: message.error
            };

            set({ trainingProgress: newProgress });

            // If training is complete, update the registration in the list
            if (message.status === 'complete') {
              console.info(`${SERVICE_TAG} Training complete, refreshing registrations`);
              get().fetchRegistrations();
            }

            // If training failed, set error
            if (message.status === 'failed') {
              console.error(`${SERVICE_TAG} Training failed`, {
                registrationId: message.registrationId,
                error: message.error
              });
              set({ error: message.error ?? 'Training failed' });
            }
        });

        set({
          trainingSubscriptionId: registrationId,
          trainingProgress: {
            registrationId,
            status: 'started',
            progress: 0
          }
        });
      },

      unsubscribeFromTrainingStatus: () => {
        const { trainingSubscriptionId } = get();

        if (!trainingSubscriptionId) {
          return;
        }

        console.info(`${SERVICE_TAG} Unsubscribing from training status`, {
          registrationId: trainingSubscriptionId
        });

        set({
          trainingSubscriptionId: null,
          trainingProgress: null
        });
      },

      /**
       * Set the capture mode (manual or video)
       * Initializes face coverage when switching to video mode
       */
      setCaptureMode: (mode: CaptureMode) => {
        const { currentRegistration, trainingIntensity } = get();
        const { diceType, totalFaces } = currentRegistration;

        console.info(`${SERVICE_TAG} Setting capture mode`, {
          mode,
          diceType,
          totalFaces,
          trainingIntensity
        });

        if (mode === 'video' && diceType) {
          // Initialize face coverage for video mode using trainingIntensity
          // TICKET-180: 'unlimited' mode has no minimum — use 1 per face as floor
          const imagesNeeded = trainingIntensity !== 'unlimited'
            ? IMAGE_REQUIREMENTS[trainingIntensity][diceType].min
            : totalFaces;
          const imagesPerFace = Math.ceil(imagesNeeded / totalFaces);

          const faceCoverage: FaceCoverage[] = Array.from(
            { length: totalFaces },
            (_, i) => ({
              faceNumber: i + 1,
              imageCount: 0,
              imagesNeeded: imagesPerFace,
              isComplete: false
            })
          );

          console.info(`${SERVICE_TAG} Initialized face coverage for video mode`, {
            totalFaces,
            imagesNeeded,
            imagesPerFace,
            trainingIntensity
          });

          set((state) => ({
            currentRegistration: {
              ...state.currentRegistration,
              captureMode: mode,
              faceCoverage,
              totalImagesNeeded: imagesNeeded,
              rolls: [],
              totalImagesCaptured: 0
            }
          }));
        } else {
          set((state) => ({
            currentRegistration: {
              ...state.currentRegistration,
              captureMode: mode
            }
          }));
        }
      },

      /**
       * Record a roll capture from video mode
       * Updates face coverage based on detected face
       */
      recordRoll: (capture: RollCapture) => {
        const { currentRegistration } = get();

        console.info(`${SERVICE_TAG} Recording roll`, {
          rollNumber: capture.rollNumber,
          detectedFace: capture.detectedFace,
          confidence: capture.confidence.toFixed(2),
          imageCount: capture.imageCount
        });

        // Update face coverage if face was detected
        let updatedFaceCoverage = [...currentRegistration.faceCoverage];
        if (capture.detectedFace !== null) {
          updatedFaceCoverage = updatedFaceCoverage.map((fc) => {
            if (fc.faceNumber === capture.detectedFace) {
              const newImageCount = fc.imageCount + capture.imageCount;
              return {
                ...fc,
                imageCount: newImageCount,
                isComplete: newImageCount >= fc.imagesNeeded
              };
            }
            return fc;
          });
        }

        const totalImagesCaptured =
          currentRegistration.totalImagesCaptured + capture.imageCount;

        set((state) => ({
          currentRegistration: {
            ...state.currentRegistration,
            rolls: [...state.currentRegistration.rolls, capture],
            faceCoverage: updatedFaceCoverage,
            totalImagesCaptured
          }
        }));
      },

      /**
       * Update the image count for a specific face
       * Used when manually adjusting coverage or during gap-filling
       */
      updateFaceCoverage: (faceNumber: number, count: number) => {
        const { currentRegistration } = get();

        console.info(`${SERVICE_TAG} Updating face coverage`, {
          faceNumber,
          count
        });

        const updatedFaceCoverage = currentRegistration.faceCoverage.map((fc) => {
          if (fc.faceNumber === faceNumber) {
            return {
              ...fc,
              imageCount: count,
              isComplete: count >= fc.imagesNeeded
            };
          }
          return fc;
        });

        // Recalculate total images captured
        const totalImagesCaptured = updatedFaceCoverage.reduce(
          (sum, fc) => sum + fc.imageCount,
          0
        );

        set((state) => ({
          currentRegistration: {
            ...state.currentRegistration,
            faceCoverage: updatedFaceCoverage,
            totalImagesCaptured
          }
        }));
      },

      /**
       * Get faces that still need more images
       * Returns faces sorted by most needed (lowest coverage first)
       */
      getMissingFaces: (): FaceCoverage[] => {
        const { currentRegistration } = get();
        const { faceCoverage } = currentRegistration;

        const missingFaces = faceCoverage
          .filter((fc) => !fc.isComplete)
          .sort((a, b) => a.imageCount - b.imageCount);

        if (missingFaces.length > 0) {
          console.info(`${SERVICE_TAG} Missing faces`, {
            count: missingFaces.length,
            faces: missingFaces.map((f) => ({
              face: f.faceNumber,
              has: f.imageCount,
              needs: f.imagesNeeded
            }))
          });
        }

        return missingFaces;
      },

      /**
       * Advance rotation position for guided capture
       * Increments rotation and sets pending flag when minimum captures complete
       * TICKET-095: No longer auto-advances face - sets pending flag for user confirmation
       */
      advanceRotation: () => {
        const { currentRegistration, trainingIntensity } = get();
        const { currentRotation, capturesPerFace, currentFace, faceProgressionPending } = currentRegistration;

        // TICKET-180: In unlimited mode, never trigger the face-progression popup — just cycle rotation
        if (trainingIntensity === 'unlimited') {
          set((state) => ({
            currentRegistration: {
              ...state.currentRegistration,
              currentRotation: (currentRotation + 1) % capturesPerFace
            }
          }));
          return;
        }

        // If progression is already pending, just increment rotation (user wants more photos)
        if (faceProgressionPending) {
          console.info(`${SERVICE_TAG} Continuing rotation (progression pending)`, {
            currentFace,
            currentRotation,
            newRotation: (currentRotation + 1) % capturesPerFace
          });

          set((state) => ({
            currentRegistration: {
              ...state.currentRegistration,
              // Cycle rotation for visual guidance even when past minimum
              currentRotation: (currentRotation + 1) % capturesPerFace
            }
          }));
          return;
        }

        const nextRotation = currentRotation + 1;

        if (nextRotation >= capturesPerFace) {
          // Minimum captures for this face complete - prompt user instead of auto-advancing
          console.info(`${SERVICE_TAG} Face captures complete, prompting user`, {
            completedFace: currentFace,
            capturesPerFace
          });

          set((state) => ({
            currentRegistration: {
              ...state.currentRegistration,
              currentRotation: 0, // Reset for visual cycling if user continues
              faceProgressionPending: true
            }
          }));
        } else {
          console.info(`${SERVICE_TAG} Advancing rotation`, {
            currentFace,
            previousRotation: currentRotation,
            nextRotation,
            capturesPerFace
          });

          set((state) => ({
            currentRegistration: {
              ...state.currentRegistration,
              currentRotation: nextRotation
            }
          }));
        }
      },

      /**
       * TICKET-095: Confirm face progression - advance to next face
       */
      confirmFaceProgression: () => {
        const { currentRegistration } = get();
        const { currentFace, totalFaces } = currentRegistration;

        const nextFace = currentFace + 1;
        const isComplete = nextFace > totalFaces;

        console.info(`${SERVICE_TAG} User confirmed face progression`, {
          completedFace: currentFace,
          nextFace: isComplete ? null : nextFace,
          isComplete
        });

        set((state) => ({
          currentRegistration: {
            ...state.currentRegistration,
            currentRotation: 0,
            currentFace: isComplete ? currentFace : nextFace,
            step: isComplete ? 'reviewing' : state.currentRegistration.step,
            faceProgressionPending: false
          }
        }));
      },

      /**
       * TICKET-095: Dismiss face progression prompt - stay on current face
       */
      dismissFaceProgression: () => {
        const { currentRegistration } = get();

        console.info(`${SERVICE_TAG} User dismissed face progression, staying on face`, {
          currentFace: currentRegistration.currentFace
        });

        // Keep faceProgressionPending true so user can continue adding photos
        // and the prompt will reappear when they manually navigate or capture
        // Actually, we should dismiss it but allow continued capture
        set((state) => ({
          currentRegistration: {
            ...state.currentRegistration,
            faceProgressionPending: false,
            currentRotation: 0 // Reset rotation cycle
          }
        }));
      },

      /**
       * Add an image to a face's image collection
       * Uploads to S3 if in cloud storage mode, then adds to local state
       * Each image gets a unique S3 key to prevent overwrites (TICKET-087)
       */
      addImageToFace: async (faceNumber: number, imageBlob: Blob) => {
        const { currentRegistration } = get();
        const { currentRotation, faces, registrationId } = currentRegistration;

        // Get storage configuration
        const { appSettings } = useSoulBondsStore.getState();
        const storageMode = appSettings.storageConfig?.mode || 'cloud';

        // Find the face data to count existing images
        const faceData = faces.find((f) => f.faceNumber === faceNumber);
        const existingImageCount = faceData?.images.length || 0;
        const imageIndex = existingImageCount + 1;

        console.info(`${SERVICE_TAG} Adding image to face`, {
          faceNumber,
          rotationIndex: currentRotation,
          blobSize: imageBlob.size,
          storageMode,
          imageIndex,
          registrationId
        });

        // Upload to S3 if in cloud mode
        let s3Key: string | undefined;
        if ((storageMode === 'cloud' || storageMode === 'both') && registrationId) {
          try {
            // Get a unique presigned URL for this specific image (TICKET-087)
            console.info(`${SERVICE_TAG} Getting presigned URL for face ${faceNumber}, image ${imageIndex}...`);
            const { uploadUrl, s3Key: uniqueS3Key } = await diceRegistryService.getImageUploadUrl(
              registrationId,
              faceNumber,
              imageIndex
            );

            console.info(`${SERVICE_TAG} Uploading to S3 for face ${faceNumber}, image ${imageIndex}...`);
            await diceRegistryService.uploadFace(uploadUrl, imageBlob);
            console.info(`${SERVICE_TAG} S3 upload complete for face ${faceNumber}, image ${imageIndex}`);

            // Use the unique S3 key returned by the backend
            s3Key = uniqueS3Key;
          } catch (error) {
            console.error(`${SERVICE_TAG} S3 upload failed for face ${faceNumber}`, {
              error: error instanceof Error ? error.message : 'Unknown error'
            });
            set({ error: `Failed to upload image for face ${faceNumber}. Please try again.` });
            return;
          }
        } else if (storageMode !== 'cloud' && storageMode !== 'both') {
          console.warn(`${SERVICE_TAG} S3 upload skipped - storageMode is '${storageMode}'`);
        }

        const newImage: FaceImage = {
          id: crypto.randomUUID(),
          blob: imageBlob,
          thumbnailUrl: URL.createObjectURL(imageBlob),
          capturedAt: new Date().toISOString(),
          rotationIndex: currentRotation,
          s3Key
        };

        set((state) => {
          const updatedFaces = state.currentRegistration.faces.map((f) => {
            if (f.faceNumber === faceNumber) {
              const updatedImages = [...f.images, newImage];
              return {
                ...f,
                images: updatedImages,
                imageBlob, // Keep legacy support
                uploaded: updatedImages.length > 0
              };
            }
            return f;
          });

          return {
            currentRegistration: {
              ...state.currentRegistration,
              faces: updatedFaces,
              totalImagesCaptured: state.currentRegistration.totalImagesCaptured + 1
            }
          };
        });
      },

      /**
       * Remove an image from a face's collection
       * Revokes object URL for memory cleanup
       * Deletes from S3 if cloud storage mode is active
       * TICKET-088: In edit mode, prevents removing the last image from a face
       * TICKET-105: Await S3 deletion before updating state to ensure sync
       */
      removeImageFromFace: async (faceNumber: number, imageId: string) => {
        const { currentRegistration } = get();
        const face = currentRegistration.faces.find((f) => f.faceNumber === faceNumber);

        if (!face) {
          console.warn(`${SERVICE_TAG} Face not found for image removal`, { faceNumber });
          return;
        }

        const imageToRemove = face.images.find((img) => img.id === imageId);
        if (!imageToRemove) {
          console.warn(`${SERVICE_TAG} Image not found for removal`, { faceNumber, imageId });
          return;
        }

        // TICKET-105: Await S3 deletion before updating local state
        // This ensures the image count stays in sync with actual S3 storage
        if (imageToRemove.s3Key && currentRegistration.registrationId) {
          const { appSettings } = useSoulBondsStore.getState();
          const storageMode = appSettings.storageConfig?.mode || 'cloud';

          if (storageMode === 'cloud' || storageMode === 'both') {
            set({ isLoading: true, error: null });

            const deleted = await diceRegistryService.deleteImage(
              currentRegistration.registrationId,
              imageToRemove.s3Key
            );

            if (!deleted) {
              console.warn(`${SERVICE_TAG} S3 deletion failed, keeping image in state`, {
                faceNumber,
                imageId,
                s3Key: imageToRemove.s3Key
              });
              set({
                isLoading: false,
                error: 'Failed to delete image from storage. Please try again.'
              });
              return;
            }

            set({ isLoading: false });
          }
        }

        console.info(`${SERVICE_TAG} Removing image from face`, {
          faceNumber,
          imageId,
          remainingCount: face.images.length - 1,
          s3Key: imageToRemove.s3Key,
          s3DeleteSuccessful: true
        });

        set((state) => {
          const updatedFaces = state.currentRegistration.faces.map((f) => {
            if (f.faceNumber === faceNumber) {
              const updatedImages = f.images.filter((img) => img.id !== imageId);
              return {
                ...f,
                images: updatedImages,
                imageBlob: updatedImages.length > 0 ? updatedImages[updatedImages.length - 1].blob : null,
                uploaded: updatedImages.length > 0
              };
            }
            return f;
          });

          return {
            currentRegistration: {
              ...state.currentRegistration,
              faces: updatedFaces,
              totalImagesCaptured: Math.max(0, state.currentRegistration.totalImagesCaptured - 1)
            }
          };
        });

        // Revoke object URL for memory cleanup AFTER state update
        // so the thumbnail stays valid until the image is removed from the UI
        if (imageToRemove.thumbnailUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(imageToRemove.thumbnailUrl);
        }
      },

      /**
       * Replace an image on a face with a new cropped version (TICKET-227)
       * Revokes old blob URL, creates new one, updates entry in place.
       * Re-uploads to S3 if the image was already uploaded (delete old + upload new).
       */
      replaceImageOnFace: async (faceNumber: number, imageId: string, newBlob: Blob) => {
        const { currentRegistration } = get();
        const face = currentRegistration.faces.find((f) => f.faceNumber === faceNumber);
        if (!face) {
          console.warn(`${SERVICE_TAG} Face not found for image replacement`, { faceNumber });
          return;
        }

        const imageIndex = face.images.findIndex((img) => img.id === imageId);
        if (imageIndex === -1) {
          console.warn(`${SERVICE_TAG} Image not found for replacement`, { faceNumber, imageId });
          return;
        }

        const oldImage = face.images[imageIndex];

        console.info(`${SERVICE_TAG} Replacing image on face`, {
          faceNumber,
          imageId,
          oldBlobSize: oldImage.blob.size,
          newBlobSize: newBlob.size,
          hasS3Key: !!oldImage.s3Key
        });

        // Create new thumbnail URL before revoking the old one
        const thumbnailUrl = URL.createObjectURL(newBlob);

        // Handle S3 re-upload if image was already uploaded
        let newS3Key = oldImage.s3Key;
        if (oldImage.s3Key && currentRegistration.registrationId) {
          const { appSettings } = useSoulBondsStore.getState();
          const storageMode = appSettings.storageConfig?.mode || 'cloud';

          if (storageMode === 'cloud' || storageMode === 'both') {
            try {
              // Delete old image from S3
              await diceRegistryService.deleteImage(
                currentRegistration.registrationId,
                oldImage.s3Key
              );

              // Get new presigned URL and upload cropped image
              const newImageIndex = imageIndex + 1;
              const { uploadUrl, s3Key: freshS3Key } = await diceRegistryService.getImageUploadUrl(
                currentRegistration.registrationId,
                faceNumber,
                newImageIndex
              );
              await diceRegistryService.uploadFace(uploadUrl, newBlob);
              newS3Key = freshS3Key;

              console.info(`${SERVICE_TAG} Cropped image re-uploaded to S3`, {
                faceNumber,
                imageId,
                oldS3Key: oldImage.s3Key,
                newS3Key
              });
            } catch (error) {
              console.warn(`${SERVICE_TAG} Failed to re-upload cropped image to S3`, {
                error: error instanceof Error ? error.message : 'Unknown error'
              });
              // Continue with local update even if S3 fails
            }
          }
        }

        // Capture old thumbnail URL before updating state
        const oldThumbnailUrl = oldImage.thumbnailUrl;

        // Update image in place
        set((state) => {
          const updatedFaces = state.currentRegistration.faces.map((f) => {
            if (f.faceNumber === faceNumber) {
              const updatedImages = [...f.images];
              updatedImages[imageIndex] = {
                ...oldImage,
                blob: newBlob,
                thumbnailUrl,
                s3Key: newS3Key
              };
              return {
                ...f,
                images: updatedImages,
                imageBlob: newBlob
              };
            }
            return f;
          });

          return {
            currentRegistration: {
              ...state.currentRegistration,
              faces: updatedFaces
            }
          };
        });

        // Revoke old blob URL AFTER state update
        // so the thumbnail stays valid until the new URL is in state
        if (oldThumbnailUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(oldThumbnailUrl);
        }
      },

      /**
       * Get all images for a specific face
       */
      getImagesForFace: (faceNumber: number): FaceImage[] => {
        const { currentRegistration } = get();
        const face = currentRegistration.faces.find((f) => f.faceNumber === faceNumber);
        return face?.images ?? [];
      },

      /**
       * Get total image count across all faces
       */
      getTotalImageCount: (): number => {
        const { currentRegistration } = get();
        return currentRegistration.faces.reduce((sum, f) => sum + f.images.length, 0);
      },

      // TICKET-071: Auto-capture toggle
      setAutoCaptureEnabled: (enabled: boolean) => {
        console.info(`${SERVICE_TAG} Auto-capture ${enabled ? 'enabled' : 'disabled'}`);
        set({ autoCaptureEnabled: enabled });
      },

      /**
       * TICKET-117: Set degrees per capture for rotation guidance
       * @param degrees - Degrees to rotate between captures (15, 30, 45, 60, 90, 180, or 360)
       */
      setDegreesPerCapture: (degrees: DegreesPerCapture) => {
        const capturesPerFace = calculateCapturesFromDegrees(degrees);

        console.info(`${SERVICE_TAG} Setting degrees per capture`, {
          degrees,
          capturesPerFace,
          willPersist: true
        });

        set((state) => ({
          currentRegistration: {
            ...state.currentRegistration,
            degreesPerCapture: degrees,
            capturesPerFace
          },
          // Save preference globally (persisted via Zustand)
          degreesPerCapturePreference: degrees
        }));
      },

      /**
       * Start edit mode for an existing registration (TICKET-088)
       * Loads existing images from S3 and populates the store for editing
       */
      startEditMode: async (deviceId: string, diceType: DiceType) => {
        set({ isLoading: true, error: null, isEditMode: true });

        try {
          console.info(`${SERVICE_TAG} Starting edit mode`, { deviceId, diceType });

          // Fetch the registration details
          const regResponse = await diceRegistryService.getRegistration(deviceId, diceType);
          const { registration } = regResponse;

          // Log status for debugging - allow editing regardless of status
          console.info(`${SERVICE_TAG} Editing registration with status`, {
            status: registration.status
          });

          // Fetch existing images from S3
          const imagesResponse = await diceRegistryService.listImages(registration.registrationId);

          // Build face capture data from existing images
          const totalFaces = FACE_COUNTS[diceType];
          const faces: FaceCaptureData[] = [];

          for (let faceNum = 1; faceNum <= totalFaces; faceNum++) {
            const faceImages = imagesResponse.images
              .filter(img => img.faceNumber === faceNum)
              .map(img => ({
                id: crypto.randomUUID(),
                blob: null as unknown as Blob, // Will be loaded on demand
                thumbnailUrl: img.downloadUrl, // Use presigned URL directly
                capturedAt: new Date().toISOString(),
                rotationIndex: img.imageIndex - 1,
                s3Key: img.s3Key
              }));

            faces.push({
              faceNumber: faceNum,
              images: faceImages,
              imageBlob: null,
              uploadUrl: '', // Not needed for edit mode
              uploaded: faceImages.length > 0
            });
          }

          // Calculate total images captured
          const totalImagesCaptured = faces.reduce((sum, f) => sum + f.images.length, 0);

          console.info(`${SERVICE_TAG} Edit mode loaded`, {
            registrationId: registration.registrationId,
            diceType,
            totalFaces,
            totalImages: totalImagesCaptured
          });

          // Use persisted preference or default based on training intensity
          // TICKET-180: 'unlimited' mode has no degree default — fall back to 45
          const { degreesPerCapturePreference, trainingIntensity } = get();
          const degreesPerCapture = degreesPerCapturePreference
            ?? (trainingIntensity !== 'unlimited' ? DEFAULT_DEGREES_BY_MODE[trainingIntensity] : 45);
          const capturesPerFace = calculateCapturesFromDegrees(degreesPerCapture);

          console.info(`${SERVICE_TAG} Edit mode using degrees`, {
            diceType,
            degreesPerCapture,
            capturesPerFace
          });

          set(() => ({
            currentRegistration: {
              ...initialCurrentRegistration,
              registrationId: registration.registrationId,
              diceType,
              diceName: registration.diceName || '',
              totalFaces,
              faces,
              step: 'reviewing', // Go directly to review for editing
              captureMode: 'manual',
              totalImagesCaptured,
              degreesPerCapture,
              capturesPerFace
            },
            isLoading: false,
            isEditMode: true
          }));
        } catch (error) {
          console.error(`${SERVICE_TAG} Failed to start edit mode`, {
            error: error instanceof Error ? error.message : 'Unknown error',
            deviceId,
            diceType
          });
          set({
            error: 'Failed to load dice set for editing. Please try again.',
            isLoading: false,
            isEditMode: false
          });
        }
      },

      /**
       * Exit edit mode and return to normal state (TICKET-088)
       */
      exitEditMode: () => {
        console.info(`${SERVICE_TAG} Exiting edit mode`);

        // Revoke any object URLs to prevent memory leaks
        const { currentRegistration } = get();
        currentRegistration.faces.forEach((face) => {
          face.images.forEach((img) => {
            // Only revoke if it's a blob URL (not a presigned S3 URL)
            if (img.thumbnailUrl && img.thumbnailUrl.startsWith('blob:')) {
              URL.revokeObjectURL(img.thumbnailUrl);
            }
          });
        });

        set({
          currentRegistration: initialCurrentRegistration,
          isEditMode: false,
          error: null
        });
      },

    }),
    {
      name: 'dicecapture-registry',
      partialize: (state) => ({
        deviceId: state.deviceId,
        registrations: state.registrations,
        autoCaptureEnabled: state.autoCaptureEnabled, // TICKET-071: Persist preference
        degreesPerCapturePreference: state.degreesPerCapturePreference, // TICKET-117: Persist degrees preference
        isEditMode: state.isEditMode, // TICKET-088: Persist edit mode across navigation
        currentRegistration: state.currentRegistration // Persist registration state across navigation
      })
    }
  )
);
